// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Appointment, Extension } from '@medplum/fhirtypes';

/** Parsed telehealth meeting information from a FHIR Appointment extension */
export interface TelehealthInfo {
  /** URL for participants to join the meeting */
  joinUrl: string;
  /** URL for the host/provider to start the meeting */
  startUrl: string;
  /** Zoom meeting ID */
  meetingId: string;
  /** Meeting password when Zoom generated one */
  password?: string;
}

/** Extension URL for Polaris telehealth meeting info stored on Appointment resources */
export const TELEHEALTH_EXTENSION_URL = 'http://polarissleep.com/telehealth';

/**
 * Extract telehealth meeting information from an Appointment's extensions.
 * @param appointment - The FHIR Appointment resource to read from
 * @returns The parsed telehealth info, or undefined if no telehealth extension is present
 */
export function getTelehealthInfo(appointment: Appointment): TelehealthInfo | undefined {
  const telehealthExt = appointment.extension?.find((ext) => ext.url === TELEHEALTH_EXTENSION_URL);
  if (!telehealthExt?.extension) {
    return undefined;
  }

  const subExts = telehealthExt.extension;
  const joinUrl = subExts.find((e) => e.url === 'join-url')?.valueUrl;
  const startUrl = subExts.find((e) => e.url === 'start-url')?.valueUrl;
  const meetingId = subExts.find((e) => e.url === 'meeting-id')?.valueString;
  const password = subExts.find((e) => e.url === 'password')?.valueString;

  if (!joinUrl || !startUrl || !meetingId) {
    return undefined;
  }

  return { joinUrl, startUrl, meetingId, password };
}

/**
 * Return a new Appointment with the telehealth extension set (does not mutate).
 * @param appointment - The FHIR Appointment resource to add the extension to
 * @param info - The telehealth meeting information to store
 * @returns A new Appointment with the telehealth extension
 */
export function setTelehealthExtension(appointment: Appointment, info: TelehealthInfo): Appointment {
  if (!isNonEmptyString(info.joinUrl) || !isNonEmptyString(info.startUrl) || !isNonEmptyString(info.meetingId)) {
    throw new Error('Telehealth info must include string joinUrl, startUrl, and meetingId values');
  }

  const telehealthExtension: Extension = {
    url: TELEHEALTH_EXTENSION_URL,
    extension: [
      { url: 'join-url', valueUrl: info.joinUrl },
      { url: 'start-url', valueUrl: info.startUrl },
      { url: 'meeting-id', valueString: info.meetingId },
      ...(isNonEmptyString(info.password) ? [{ url: 'password', valueString: info.password }] : []),
    ],
  };

  // Replace existing telehealth extension or append
  const otherExtensions = (appointment.extension ?? []).filter((ext) => ext.url !== TELEHEALTH_EXTENSION_URL);

  return {
    ...appointment,
    extension: [...otherExtensions, telehealthExtension],
  };
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}
